import type {
  DataZoomComponentOption,
  GridComponentOption,
  MarkAreaComponentOption,
  MarkLineComponentOption,
  TooltipComponentOption,
  XAXisComponentOption,
  YAXisComponentOption,
} from 'echarts'

export interface ChartDataZoom {
  start: number
  end: number
}

/** Formata timestamp/valor para rótulo do eixo X (pt-BR dd/MM HH:mm). */
export function formatAxisDate(val: number | string): string {
  let ms = typeof val === 'string' ? Number(val) : val
  if (Number.isNaN(ms)) return String(val)
  if (ms > 0 && ms < 1e12) ms *= 1000
  return new Date(ms).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Formata timestamp como HH:mm:ss (pt-BR) para cabeçalho de tooltip. */
export function formatTimeTooltip(ms: number): string {
  return new Date(ms).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

/** markArea de um único período de alarme (detalhe do alarme). */
export function buildAlarmMarkArea(alarmPeriod?: [number, number] | null): MarkAreaComponentOption | undefined {
  if (!alarmPeriod || alarmPeriod[1] <= alarmPeriod[0]) return undefined
  return {
    silent: true,
    label: { show: false },
    data: [
      [
        {
          xAxis: alarmPeriod[0],
          itemStyle: {
            color: 'rgba(254, 202, 202, 0.55)',
            borderColor: 'rgba(185, 28, 28, 0.85)',
            borderWidth: 2,
          },
        },
        { xAxis: alarmPeriod[1] },
      ],
    ],
  }
}

/** markArea de períodos de alarme com destaque por hover (gráficos de sensor). */
export function buildHoveredAlarmMarkArea(
  alarmPeriods: [number, number][] | null | undefined,
  hoveredIndex: number | null,
): MarkAreaComponentOption | undefined {
  if (!alarmPeriods || alarmPeriods.length === 0) return undefined
  return {
    silent: true,
    data: alarmPeriods.map(([start, end], idx) => {
      const isHighlighted = idx === hoveredIndex
      return [
        {
          xAxis: start,
          itemStyle: isHighlighted
            ? { color: 'rgba(220, 38, 38, 0.35)', borderColor: 'rgba(185, 28, 28, 0.9)', borderWidth: 2 }
            : { color: 'rgba(220, 38, 38, 0.14)', borderColor: 'rgba(220, 38, 38, 0.45)', borderWidth: 1 },
        },
        { xAxis: end },
      ]
    }),
  }
}

/** markLine vertical de início e fim de um período de alarme. */
export function buildAlarmMarkLine(alarmPeriod?: [number, number] | null): MarkLineComponentOption | undefined {
  if (!alarmPeriod || alarmPeriod[1] <= alarmPeriod[0]) return undefined
  return {
    silent: true,
    symbol: 'none',
    label: { show: false },
    lineStyle: { color: 'rgba(185, 28, 28, 0.85)', width: 2 },
    data: [{ xAxis: alarmPeriod[0] }, { xAxis: alarmPeriod[1] }],
  }
}

export interface BuildDataZoomParams {
  dataZoom: ChartDataZoom
  accentColor: string
  fillerColor: string
  sliderBottom?: number
  showMoveHandle?: boolean
}

/** dataZoom padrão dos gráficos de sensor (inside + slider). */
export function buildDataZoom(params: BuildDataZoomParams): DataZoomComponentOption[] {
  const { dataZoom, accentColor, fillerColor, sliderBottom = 8, showMoveHandle = false } = params
  return [
    {
      type: 'inside',
      xAxisIndex: 0,
      start: dataZoom.start,
      end: dataZoom.end,
      zoomOnMouseWheel: true,
      moveOnMouseMove: true,
      preventDefaultMouseMove: true,
    },
    {
      type: 'slider',
      xAxisIndex: 0,
      start: dataZoom.start,
      end: dataZoom.end,
      height: 20,
      bottom: sliderBottom,
      borderColor: 'transparent',
      fillerColor,
      handleStyle: { color: accentColor },
      dataBackground: { lineStyle: { opacity: 0.3 }, areaStyle: { opacity: 0.05 } },
      ...(showMoveHandle ? { moveHandleSize: 6 } : {}),
      textStyle: { fontSize: 10 },
    },
  ]
}

/** Eixo X de tempo padrão dos gráficos de sensor. */
export function buildTimeXAxis(): XAXisComponentOption {
  return {
    type: 'time',
    boundaryGap: [0, 0] as [string | number, string | number],
    axisLabel: { fontSize: 10, formatter: (value) => formatAxisDate(value) },
  }
}

export interface BuildValueYAxisParams {
  unit: string
  min?: number
  max?: number
}

/** Eixo Y de valor padrão (nome da unidade + linhas tracejadas). */
export function buildValueYAxis(params: BuildValueYAxisParams): YAXisComponentOption {
  return {
    type: 'value',
    name: params.unit,
    nameLocation: 'end',
    nameGap: 8,
    min: params.min,
    max: params.max,
    axisLabel: { fontSize: 10 },
    splitLine: { lineStyle: { type: 'dashed', opacity: 0.4 } },
  }
}

export interface BuildChartGridParams {
  top?: string
  left?: string
  right?: string
}

/** Grid padrão dos gráficos de sensor. */
export function buildChartGrid(params: BuildChartGridParams = {}): GridComponentOption {
  return {
    left: params.left ?? '1%',
    right: params.right ?? '1%',
    bottom: '22%',
    top: params.top ?? '14%',
    containLabel: true,
  }
}

/** Tooltip de múltiplas séries com unidade (ex.: V, A). */
export function buildMultiSeriesTooltipFormatter(unit: string, decimals = 2): NonNullable<TooltipComponentOption['formatter']> {
  return (params: any) => {
    const arr = Array.isArray(params) ? params : [params]
    const lines = arr
      .filter((p: any) => p?.value?.[1] != null)
      .map((p: any) => `${p.seriesName}: <strong>${Number(p.value[1]).toFixed(decimals)} ${unit}</strong>`)
    if (lines.length === 0) return ''
    const date = arr[0] ? new Date(arr[0].value[0]) : new Date()
    return `${formatTimeTooltip(date.getTime())}<br/>${lines.join('<br/>')}`
  }
}
